import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const FLOAT_MENU = ({ anchorEl, open, onClose, children }) => {
  const menuRef = useRef(null);
  const [render, setRender] = useState(open);
  const [animateOpen, setAnimateOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  // Mount/unmount logic with animation
  useEffect(() => {
    if (open) {
      setRender(true);
      setTimeout(() => setAnimateOpen(true), 10); // smoother open
    } else {
      setAnimateOpen(false);
      const timeout = setTimeout(() => setRender(false), 180);
      return () => clearTimeout(timeout);
    }
  }, [open]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      // ADD THIS CHECK:
      // If the element that was clicked is the trigger button (or is inside it),
      // do nothing. Let the button's own onClick event handle the state change.
      if (anchorEl && anchorEl.contains(event.target)) {
        return;
      }

      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose?.();
      }
    };
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, onClose, anchorEl]);

  // Close on ESC
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose?.();
      }
    };
    if (open) {
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  // Positioning
  useEffect(() => {
    if (!render || !anchorEl || !anchorEl.getBoundingClientRect) return;

    const rect = anchorEl.getBoundingClientRect();
    const menu = menuRef.current;
    if (!menu) return;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const scrollX = window.scrollX || window.pageXOffset;
    const scrollY = window.scrollY || window.pageYOffset;

    const menuWidth = menu.offsetWidth;
    const menuHeight = menu.offsetHeight;

    let top = rect.bottom + scrollY;
    let left = rect.left + scrollX;

    if (left + menuWidth > scrollX + viewportWidth) {
      left = Math.max(scrollX, rect.right - menuWidth + scrollX);
    }
    if (top + menuHeight > scrollY + viewportHeight) {
      top = Math.max(scrollY, rect.top - menuHeight + scrollY);
    }
    if (left < scrollX) left = scrollX;
    if (top < scrollY) top = scrollY;

    setPosition({ top, left });
  }, [render, anchorEl]);

  if (!render || !anchorEl) return null;

  const menuElement = (
    <div
      ref={menuRef}
      style={{
        position: "absolute",
        top: position.top,
        left: position.left,
        zIndex: 9999,
        background: "#fff",
        boxShadow: "0 2px 12px rgba(0, 0, 0, 0.15)",
        borderRadius: "13px",
        padding: "13px",
        transformOrigin: "top left",
        transition: "transform 180ms ease-out, opacity 180ms ease-out",
        transform: animateOpen ? "scale(1)" : "scale(0.8)",
        opacity: animateOpen ? 1 : 0,
        pointerEvents: animateOpen ? "auto" : "none",
        maxWidth: "80vw",
      }}
    >
      {children}
    </div>
  );

  return createPortal(menuElement, document.body);
};

export default FLOAT_MENU;
